/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { formatDate } from '@squiz/dxp-console-support';
import { ActionMenu, ActionMenuProps, EmptyState, EmptyStateProps, ICON_TRASH, Table, TableProps } from '@squiz/sds';
import { AI_IDS, AI_STRINGS } from 'apps/artificial-intelligence/src/Constants/Strings.constants';
import clsx from 'clsx';
import { Row, RowProps } from 'libs/sds/src/Components';
import { ICON_CONTENT_PASTE_WITH_LIST } from 'libs/sds/src/Icons/Combination/ContentPasteWithList';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { SemanticAuditorCreateTestDialogLauncher } from '../SemanticAuditorCreateTestDialog/SemanticAuditorCreateTestDialogLauncher/SemanticAuditorCreateTestDialogLauncher';
import { SEMANTIC_AUDITOR_TEST_LIST_DATA } from './SemanticAuditorTestListData.mock';

export const SemanticAuditorTestList = (): JSX.Element => {
  const { t } = useTranslation();
  const labels = {
    createAndRunToAudit: t(AI_STRINGS.createAndRunToAudit),
    createTest: t(AI_STRINGS.createTest),
    dateCreated: t(AI_STRINGS.dateCreated),
    delete: t(AI_STRINGS.delete),
    lastRun: t(AI_STRINGS.lastRun),
    name: t(AI_STRINGS.name),
    noTestsHaveBeenCreatedYet: t(AI_STRINGS.noTestsHaveBeenCreatedYet),
    questions: t(AI_STRINGS.questions),
    tests: t(AI_STRINGS.tests),
    totalRuns: t(AI_STRINGS.totalRuns),
  };

  useEffect(() => {
    const actionsColumnHeader = document.querySelector(
      'th[aria-label="actions: activate to sort column ascending"]',
    ) as HTMLTableCellElement;
    actionsColumnHeader.setAttribute('tabIndex', '-1');
  }, []);

  const showEmptyState = !SEMANTIC_AUDITOR_TEST_LIST_DATA.length;
  const tableRows = SEMANTIC_AUDITOR_TEST_LIST_DATA.map((test, i) => {
    const MIN_ITEMS_TO_INVERT_POSITION = 5;
    const ROWS_FROM_BOTTOM = 2;
    const dropdownPositionUp =
      SEMANTIC_AUDITOR_TEST_LIST_DATA.length > MIN_ITEMS_TO_INVERT_POSITION &&
      i > SEMANTIC_AUDITOR_TEST_LIST_DATA.length - ROWS_FROM_BOTTOM;

    const actionMenuProps: ActionMenuProps = {
      buttonLabel: 'Actions',
      classes: clsx('semantic-auditor-test-list__item-action-menu', {
        'semantic-auditor-test-list__item-action-menu--dropdown-up': dropdownPositionUp,
      }),
      itemList: [
        {
          // eslint-disable-next-line no-console
          action: () => console.log('Delete this test'),
          dataID: AI_IDS.deleteTest,
          icon: ICON_TRASH,
          key: labels.delete,
          label: labels.delete,
        },
      ],
      sectionKey: 'actions',
    };

    return {
      actions: <ActionMenu {...actionMenuProps} />,
      dateCreated: formatDate(test.dateCreated, true),
      lastRun: formatDate(test.lastRun, true),
      name: (
        <Link className="semantic-auditor-test-list__item-link" to={'https://google.com'}>
          {test.name}
        </Link>
      ),
      questions: test.questions,
      totalRuns: test.totalRuns,
    };
  });

  const tableProps: TableProps = {
    classes: 'semantic-auditor-test-list__table',
    isPaginated: true,
    isSlim: true,
    tableContent: {
      columns: [
        {
          content: labels.name,
          key: 'name',
        },
        {
          content: labels.questions,
          key: 'questions',
        },
        {
          content: labels.totalRuns,
          key: 'totalRuns',
        },
        {
          content: labels.lastRun,
          key: 'lastRun',
        },
        {
          content: labels.dateCreated,
          key: 'dateCreated',
        },
        {
          content: '',
          key: 'actions',
        },
      ],
      rows: tableRows,
    },
  };
  const createTestButton = <SemanticAuditorCreateTestDialogLauncher />;
  const rowProps: RowProps = {
    border: false,
    classes: 'semantic-auditor-test-list',
    subtitle: labels.createAndRunToAudit,
    title: labels.tests,
    ...(!showEmptyState && { button: createTestButton }),
  };

  const emptyStateProps: EmptyStateProps = {
    caption: labels.noTestsHaveBeenCreatedYet,
    children: createTestButton,
    isLite: true,
    svg: ICON_CONTENT_PASTE_WITH_LIST,
  };
  // TODO: Test when we've got dynamic data
  // istanbul ignore next
  return <Row {...rowProps}>{showEmptyState ? <EmptyState {...emptyStateProps} /> : <Table {...tableProps} />}</Row>;
};
